//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  addContentSourceProductsToCart,
  findContentSourceProducts,
  getContentSourceCartSummary,
  getContentSourceEligibleReviewsByProduct,
  getContentSourceProductReviewMedia,
  getContentSourceTaskDetails,
  removeAllContentSourceProductsFromCart,
  removeContentSourceProductFromCart,
  submitContentSourceOrder,
} from '@apis/campaigns/contentSourceApi';
import {
  ContentSourceAddProductsToCartRequest,
  ContentSourceCartSummaryResponse,
  ContentSourceEligibleReviewsByProductRequest,
  ContentSourceEligibleReviewsByProductResponse,
  ContentSourceProductReviewMediaRequest,
  ContentSourceProductReviewMediaResponse,
  ContentSourceProductStatus,
  ContentSourceProductsRequest,
  ContentSourceProductsResponse,
  ContentSourceRemoveProductFromCartRequest,
  ContentSourceTaskStatus,
} from '@apis/campaigns/contentSourceApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';
import { wait } from '@utils/wait';

export function useContentSourceProductsQuery<
  T = ContentSourceProductsResponse,
>(
  req: ContentSourceProductsRequest,
  select?: (data: ContentSourceProductsResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.ContentSource,
      QueryKeyConstants.ContentSourceFindProducts,
      req,
    ],
    queryFn: async ({ signal }) => {
      return findContentSourceProducts(req, { signal });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useContentSourceEligibleReviewsByProductQuery<
  T = ContentSourceEligibleReviewsByProductResponse,
>(
  req: ContentSourceEligibleReviewsByProductRequest,
  select?: (data: ContentSourceEligibleReviewsByProductResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.ContentSource,
      QueryKeyConstants.ContentSourceGetEligibleReviewsByProduct,
      req,
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(req.casProductId)) {
        throw new Error('casProductId is required for this query');
      }

      return getContentSourceEligibleReviewsByProduct(req, { signal });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.casProductId),
  });
}

export function useContentSourceProductReviewMediaQuery<
  T = ContentSourceProductReviewMediaResponse,
>(
  req: ContentSourceProductReviewMediaRequest,
  select?: (data: ContentSourceProductReviewMediaResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.ContentSource,
      QueryKeyConstants.ContentSourceGetProductReviewMedia,
      req,
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(req.casProductId)) {
        throw new Error('casProductId is required for this query');
      }

      return getContentSourceProductReviewMedia(req, { signal });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.casProductId),
  });
}

export function useContentSourceCartSummaryQuery<
  T = ContentSourceCartSummaryResponse,
>(select?: (data: ContentSourceCartSummaryResponse) => T) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.ContentSource,
      QueryKeyConstants.ContentSourceGetCartSummary,
    ],
    queryFn: async ({ signal }) => {
      return getContentSourceCartSummary({ signal });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

async function pollForTaskCompletion(
  task: () => Promise<{ taskUid?: number }>,
): Promise<void> {
  const maxRetries = 20;
  const retryDelay = 500;
  let retries = 0;

  const { taskUid } = await task();

  while (retries < maxRetries) {
    try {
      const { status } = await getContentSourceTaskDetails({ taskUid });

      if (status === ContentSourceTaskStatus.Succeeded) {
        return;
      } else if (
        status === ContentSourceTaskStatus.Failed ||
        status === ContentSourceTaskStatus.Canceled
      ) {
        throw new Error(`Task ${taskUid} failed with status: ${status}`);
      } else {
        retries++;
        await wait(retryDelay);
      }
    } catch (_e) {
      retries++;
      await wait(retryDelay);
    }
  }
  throw new Error(
    `Polling for task ${taskUid} timed out after ${maxRetries} retries.`,
  );
}

export function useContentSourceRemoveProductFromCartMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryKey = [
    QueryKeyConstants.ContentSource,
    QueryKeyConstants.ContentSourceFindProducts,
  ];

  const mutationFn = async (
    request: ContentSourceRemoveProductFromCartRequest,
  ) => {
    await pollForTaskCompletion(() =>
      removeContentSourceProductFromCart(request),
    );
  };

  return useMutation({
    mutationFn,
    onMutate: async ({ casProductId }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousMetadata = queryClient.getQueryData(queryKey);

      queryClient.setQueriesData(
        { queryKey },
        (data: ContentSourceProductsResponse | undefined) => {
          return {
            results:
              data?.results.map((product) => {
                if (product.casProductId === casProductId) {
                  return {
                    ...product,
                    status: ContentSourceProductStatus.None,
                  };
                }
                return product;
              }) ?? [],
            total: data?.total ?? 0,
          };
        },
      );

      return previousMetadata;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.ContentSource],
      });
    },
    onError: (_, __, previousData) => {
      queryClient.setQueryData(queryKey, previousData);
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed to Remove Product from Cart',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Removed from Cart',
          message:
            'The reviews and review media for 1 product were removed from your cart.',
          type: ToastType.DELETE,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useContentSourceRemoveAllProductsFromCartMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryKey = [
    QueryKeyConstants.ContentSource,
    QueryKeyConstants.ContentSourceFindProducts,
  ];

  const mutationFn = async () => {
    await pollForTaskCompletion(() => removeAllContentSourceProductsFromCart());
  };

  return useMutation({
    mutationFn,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });

      const previousMetadata = queryClient.getQueryData(queryKey);

      queryClient.setQueriesData(
        { queryKey },
        (data: ContentSourceProductsResponse | undefined) => {
          return {
            results:
              data?.results.map((product) => {
                return {
                  ...product,
                  status: ContentSourceProductStatus.None,
                };
              }) ?? [],
            total: data?.total ?? 0,
          };
        },
      );

      return previousMetadata;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.ContentSource],
      });
    },
    onError: (_, __, previousData) => {
      queryClient.setQueryData(queryKey, previousData);
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed to Remove all Products from Cart',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Removed from Cart',
          message:
            'The reviews and review media for all products were removed from your cart.',
          type: ToastType.DELETE,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useContentSourceAddProductsToCartMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryKey = [
    QueryKeyConstants.ContentSource,
    QueryKeyConstants.ContentSourceFindProducts,
  ];

  const mutationFn = async (request: ContentSourceAddProductsToCartRequest) => {
    await pollForTaskCompletion(() => addContentSourceProductsToCart(request));
  };

  const products = (numberOfProducts: number) =>
    pluralize(numberOfProducts, 'Product', 'Products', false);

  return useMutation({
    mutationFn,
    onMutate: async ({ casProductIds }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousMetadata = queryClient.getQueryData(queryKey);

      queryClient.setQueriesData(
        { queryKey },
        (data: ContentSourceProductsResponse | undefined) => {
          return {
            results:
              data?.results.map((product) => {
                if (
                  isUndefined(casProductIds) ||
                  (isDefined(product.casProductId) &&
                    casProductIds?.includes(product.casProductId))
                ) {
                  return {
                    ...product,
                    status: ContentSourceProductStatus.InCart,
                  };
                }
                return product;
              }) ?? [],
            total: data?.total ?? 0,
          };
        },
      );

      return previousMetadata;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.ContentSource],
      });
    },
    onError: (_, { casProductIds }, previousData) => {
      queryClient.setQueryData(queryKey, previousData);
      const numberOfProducts = casProductIds?.length ?? 0;
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: `Add ${products(numberOfProducts)} To Cart Failed`,
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSuccess: (_, { casProductIds }) => {
      const numberOfProducts = casProductIds?.length ?? 0;

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Added To Cart',
          message: `The reviews and review media for ${numberOfProducts > 0 ? pluralize(numberOfProducts, 'product') : 'all products'} were added to your cart.`,
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useContentSourceSubmitOrderMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const mutationFn = async () => {
    await pollForTaskCompletion(() => submitContentSourceOrder());
  };

  return useMutation({
    mutationFn,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.ContentSource],
      });
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Submit request failed',
          message: 'Try again, or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}
